import { useEffect, useState } from "react";
import { Board } from "./Board.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { Keyboard } from "./Keyboard.tsx";
import { ResultSheet } from "./ResultSheet.tsx";
import { ScreenHeader } from "./ScreenHeader.tsx";
import { SoloModeTabs } from "./SoloModeTabs.tsx";
import { Toast } from "./Toast.tsx";
import { useSoloGame } from "../features/solo/useSoloGame.ts";
import type { SoloMode } from "../features/solo/soloMode.ts";
import { useBoardSelectionHint } from "../features/useBoardSelectionHint.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { messages } from "../i18n/index.ts";
import { leaveResultThroughAd } from "../lib/adGate.ts";

interface ClassicSoloViewProps {
  onBack: () => void;
  onExitConfirmed: () => void;
  onModeChange: (mode: SoloMode) => void;
  registerBackHandler: RegisterBackHandler;
}

export function ClassicSoloView({
  onBack,
  onExitConfirmed,
  onModeChange,
  registerBackHandler,
}: ClassicSoloViewProps) {
  const game = useSoloGame();
  const [resultDismissed, setResultDismissed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const finished = game.status === "won" || game.status === "lost";
  const selectionHint = useBoardSelectionHint(finished ? null : game.pinned);

  useEffect(() => {
    if (!finished) setResultDismissed(false);
  }, [finished]);

  const hasProgress = game.rows.length > 0 || game.cells.some((cell) => cell !== undefined);

  useEffect(
    () =>
      registerBackHandler(() => {
        if (confirmOpen || switchOpen) {
          setConfirmOpen(false);
          setSwitchOpen(false);
          return "block";
        }
        if (finished && game.revealed && !resultDismissed) {
          void leaveResultThroughAd().then(() => setResultDismissed(true));
          return "block";
        }
        // Hiç tahmin yapılmamışsa ve satır boşsa kaybedilecek bir şey yok.
        if (game.status === "playing" && hasProgress) {
          setConfirmOpen(true);
          return "block";
        }
        return "allow";
      }),
    [confirmOpen, finished, game.revealed, game.status, hasProgress, registerBackHandler, resultDismissed, switchOpen],
  );

  const requestModeChange = (mode: SoloMode) => {
    if (game.status === "playing" && hasProgress) setSwitchOpen(true);
    else onModeChange(mode);
  };
  const tabs = <SoloModeTabs value="classic" onChange={requestModeChange} />;

  if (game.status === "loading") {
    return (
      <div className="app solo-app">
        <ScreenHeader title={messages.solo.title} onBack={onBack} />
        {tabs}
        <main className="grid place-items-center">
          <p className="text-sm text-muted">{messages.solo.loadingWords}</p>
        </main>
      </div>
    );
  }

  if (game.status === "error") {
    return (
      <div className="app solo-app">
        <ScreenHeader title={messages.solo.title} onBack={onBack} />
        {tabs}
        <main className="grid place-items-center px-6 text-center">
          <div>
            <p className="font-display text-lg font-bold">{messages.solo.listFailed}</p>
            <p className="mt-2 text-sm text-muted">
              {messages.solo.listFailedHint}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app solo-app">
      <ScreenHeader title={messages.solo.title} subtitle={messages.solo.unlimited} onBack={onBack} />
      {tabs}

      <main className="relative grid min-h-0 place-items-center px-3">
        <Toast message={game.toast ?? selectionHint} />
        <Board
          rows={game.rows}
          cells={game.cells}
          pinned={game.pinned}
          onSelectCell={game.selectCell}
          shake={game.shake}
          won={game.status === "won"}
        />
      </main>

      {finished && resultDismissed ? (
        <div className="finish-bar">
          <button type="button" className="btn-secondary" onClick={() => setResultDismissed(false)}>
            {messages.solo.showResult}
          </button>
          <button type="button" className="btn-primary" onClick={() => { void leaveResultThroughAd().then(game.newGame); }}>
            {messages.solo.newWord}
          </button>
        </div>
      ) : (
        <Keyboard states={game.keyboard} onPress={game.press} disabled={finished} />
      )}

      <ResultSheet
        open={finished && game.revealed && !resultDismissed}
        won={game.status === "won"}
        answer={game.answer}
        rows={game.rows}
        share={{
          mode: "practice",
          solved: game.status === "won",
          attempts: game.rows.length,
        }}
        onClose={() => { void leaveResultThroughAd().then(() => setResultDismissed(true)); }}
        onNewGame={() => {
          void leaveResultThroughAd().then(() => { setResultDismissed(true); game.newGame(); });
        }}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={messages.solo.exitTitle}
        message={messages.solo.exitMessage}
        confirmLabel={messages.solo.exit}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          onExitConfirmed();
        }}
      />

      <ConfirmDialog
        open={switchOpen}
        title={messages.solo.switchTitle}
        message={messages.solo.switchMessage}
        confirmLabel={messages.solo.switchConfirm}
        onCancel={() => setSwitchOpen(false)}
        onConfirm={() => {
          setSwitchOpen(false);
          onModeChange("timed");
        }}
      />
    </div>
  );
}
